/**
 * Shared utility functions for student accommodation logic.
 * Used by CourseCard and ManageAccommodations (and any future consumers).
 */

// =============================================================================
// Date range + course match + approved status check
// =============================================================================

/**
 * Returns true if the accommodation's date range overlaps with the course's date range.
 * If both accommodation dates are null the accommodation is considered always applicable.
 */
export const isAccommodationWithinCourseDuration = (
  accommodationStartDate,
  accommodationEndDate,
  courseStartDate,
  courseEndDate
) => {
  const crseStartDate = courseStartDate
    ? new Date(courseStartDate)
    : null;
  const crseEndDate = courseEndDate
    ? new Date(courseEndDate)
    : null;

  const isNullDates =
    accommodationStartDate === null && accommodationEndDate === null;
    
  if (isNullDates) return true;

  const startDate = accommodationStartDate
    ? new Date(accommodationStartDate)
    : null;
  const endDate = accommodationEndDate
    ? new Date(accommodationEndDate)
    : null;

  return (
    (endDate && endDate >= crseStartDate && endDate <= crseEndDate) ||
    (!startDate && endDate && endDate >= crseStartDate) ||
    (startDate && startDate >= crseStartDate && startDate <= crseEndDate) ||
    (!endDate && startDate && startDate <= crseEndDate) ||
    (startDate &&
      endDate &&
      startDate <= crseStartDate &&
      endDate >= crseEndDate)
  );
};

/**
 * Returns true if the accommodation record applies to the given course:
 *   - date range overlaps the course dates
 *   - courseId matches (or is null/undefined → applies to all courses)
 *   - status is "approved" or null
 */
export const isAccommodationApplicableToCourse = (
  accommodation,
  courseId,
  courseStartDate,
  courseEndDate
) => {
  const isWithinDateRange = isAccommodationWithinCourseDuration(
    accommodation.accommodationStartDate,
    accommodation.accommodationEndDate,
    courseStartDate,
    courseEndDate
  );

  const courseMatch =
    accommodation.courseId === null ||
    accommodation.courseId === undefined ||
    accommodation.courseId === courseId;

  const isApproved =
    accommodation.status?.toLowerCase() === "approved" ||
    accommodation.status === null;

  return isWithinDateRange && courseMatch && isApproved;
};

/**
 * Builds the course-level accommodation view for a single course.
 *
 * Returns the applicable accommodation records grouped by student IRN, along with
 * sets identifying which IRNs have at least one applicable accommodation and which
 * still have unacknowledged accommodations for that course.
 */
export const getAccommodationsForCourse = (
  course,
  studentAccommodationsInput
) => {
  const accommodationsByIrn = {};
  const irnsWithAccommodations = new Set();
  const irnsWithUnacknowledgedAccommodations = new Set();

  course.accommodatedStudentIrnList?.forEach((irn) => {
    const irnKey = `${irn}`;
    const studentAccommodation = studentAccommodationsInput?.find(
      (accommodation) => `${accommodation.studentIrn}` === irnKey
    );
    const acknowledgementRecords =
      studentAccommodation?.facultyAcknowledgements ?? [];

    if (!acknowledgementRecords.length) {
      return;
    }

    const applicableAcknowledgements = acknowledgementRecords.filter(
      (accommodationType) =>
        isAccommodationApplicableToCourse(
          accommodationType,
          course.courseId,
          course.startDate,
          course.endDate
        )
    );

    if (!applicableAcknowledgements.length) {
      return;
    }

    accommodationsByIrn[irnKey] = {
      ...(studentAccommodation || { studentIrn: irnKey }),
      facultyAcknowledgements: applicableAcknowledgements,
    };
    irnsWithAccommodations.add(irn);

    if (
      applicableAcknowledgements.some(
        (accommodationType) => !accommodationType.dateAcknowledged
      )
    ) {
      irnsWithUnacknowledgedAccommodations.add(irn);
    }
  });

  return {
    accommodationsByIrn,
    irnsWithAccommodations,
    irnsWithUnacknowledgedAccommodations,
  };
};

// =============================================================================
// SRM data → facultyAcknowledgements array
// =============================================================================

/**
 * Converts the SRM API response object into the facultyAcknowledgements array
 * format used throughout the accommodations feature.
 */
export const mapSrmDataToAcknowledgements = (srmData) => {
  const facultyAcknowledgements = [];

  const push = (
    disabilityType,
    accommodationType,
    accommodationStartDate,
    accommodationEndDate,
    courseId,
    status,
    specialArrangements,
    active
  ) => {
    if (!accommodationType) return;

    accommodationType
      .split(";")
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => {
        facultyAcknowledgements.push({
          accommodationType: item,
          disabilityType: disabilityType ?? "ADA",
          accommodationStartDate: accommodationStartDate ?? null,
          accommodationEndDate: accommodationEndDate ?? null,
          courseId: courseId ?? null,
          status: status ?? null,
          dateAcknowledged: null,
          specialArrangements: specialArrangements ?? null,
          active: active ?? false,
        });
      });
  };

  // Permanent accommodations
  push(
    srmData?.disabilityType,
    srmData?.accommodationType,
    null,
    srmData?.accomEndDate,
    null,
    srmData?.status ?? null,
    srmData?.specialArrangements ?? null,
    srmData?.isActive ?? false
  );

  push(
    srmData?.disabilityType,
    srmData?.specialArrangements,
    null,
    srmData?.accomEndDate,
    null,
    srmData?.status ?? null,
    null,
    srmData?.isActive ?? false
  );

  // Temporary accommodations
  (srmData?.tempAccoms ?? []).forEach((temp) => {
    push(
      srmData?.disabilityType,
      temp?.accommodationType,
      temp?.startDate,
      temp?.endDate,
      temp?.courseId,
      temp?.status ?? null,
      temp?.specialArrangements ?? null,
      temp?.isActive ?? false
    );

    push(
      srmData?.disabilityType,
      temp?.specialArrangements,
      temp?.startDate,
      temp?.endDate,
      temp?.courseId,
      temp?.status ?? null,
      null,
      temp?.isActive ?? false
    );
  });

  return facultyAcknowledgements;
};

